/**
 * Transport-aware wrapper for the catalog READ (`CatalogApi.list`).
 *
 * Wraps the REST `CatalogApi` so `list()` takes the fast GraphQL path when the
 * central transport registry says so (`resolveTransport("catalog.list", …)`),
 * and TRANSPARENTLY falls back to REST on any GraphQL error — so flipping the
 * flag on can never harden into an outage. Everything else on the CatalogApi
 * (create/update/remove, assignments, primaries, logos) is delegated untouched:
 * writes and un-modeled reads stay on REST.
 *
 * GraphQL is used only when ALL hold, else REST:
 *   - a GraphQL client is configured,
 *   - the registry resolves the operation to "graphql",
 *   - the kind is one GraphQL serves (entity/tag/label/group — not address),
 *   - the query uses no filter GraphQL doesn't model yet (chain/lifecycle/cursor).
 */
import type { CatalogApi, CatalogListQuery, CatalogRow } from "@authe/catalog";
import type { CatalogItemKind } from "@authe/fe-commons/catalog";
import type { FeatureFlags } from "@authe/fe-commons/flags";
import type { GraphQLClient } from "@authe/fe-commons/graphql";
import { catalogKindToGraphql, fetchCatalogItems, GraphQLRequestError } from "@authe/fe-commons/graphql";
import { resolveTransport } from "@authe/fe-commons/transport";
import { createLogger } from "@authe/fe-commons/logging";

const log = createLogger("dashboard:graphql-catalog");

export interface TransportAwareCatalogOptions {
  /** The GraphQL read client, or `null` when unconfigured (always REST). */
  graphqlClient: GraphQLClient | null;
  /** The live feature-flag set (registry precedence: master + per-op). */
  flags: FeatureFlags;
}

/** Filters the GraphQL `catalogItems` read does not model — force REST when set. */
function usesUnmodeledFilter(query: CatalogListQuery): boolean {
  return Boolean(query.chain || query.lifecycle || query.cursor);
}

export function withGraphqlCatalogList(
  rest: CatalogApi,
  { graphqlClient, flags }: TransportAwareCatalogOptions,
): CatalogApi {
  return {
    ...rest,
    async list(kind: CatalogItemKind, query: CatalogListQuery = {}): Promise<CatalogRow[]> {
      const graphqlServesKind = catalogKindToGraphql(kind) != null;
      const wantsGraphql =
        graphqlClient != null &&
        graphqlServesKind &&
        !usesUnmodeledFilter(query) &&
        resolveTransport("catalog.list", flags, { graphqlConfigured: true }) === "graphql";

      if (wantsGraphql && graphqlClient) {
        try {
          return await fetchCatalogItems(graphqlClient, kind, {
            search: query.search,
            limit: query.limit,
          });
        } catch (err) {
          // Transport/GraphQL failure → fall back to REST. Anything else (a
          // programming error) surfaces so it isn't silently masked.
          if (!(err instanceof GraphQLRequestError)) throw err;
          log.warn("catalog.list GraphQL failed, falling back to REST", {
            kind,
            status: err.status,
            message: err.message,
          });
        }
      }
      return rest.list(kind, query);
    },
  };
}
